/**
 * Enhanced Product Image Zoom with Magnifying Lens
 * 
 * Adds advanced zoom functionality to product specification images
 * Features: Click to enlarge, magnifying lens on hover/touch in modal, pinch-to-zoom support
 */

class ProductImageZoom {
  constructor() {
    this.zoomableImages = document.querySelectorAll('.viewer-holder img.product-image');
    this.modalId = 'ImageZoomModal';
    this.zoomLevel = 1;
    this.maxZoom = 4;
    this.minZoom = 1;
    this.isDragging = false;
    this.lastMousePos = { x: 0, y: 0 };
    this.imageOffset = { x: 0, y: 0 };
    
    this.init();
  }

  init() {
    // Create modal element if it doesn't exist
    if (!document.getElementById(this.modalId)) {
      this.createModal();
    }

    // Add click event listeners to all product images
    this.zoomableImages.forEach(image => {
      if (!image.dataset.zoomInitialized) {
        image.classList.add('zoomable-image');
        image.addEventListener('click', this.openZoom.bind(this));
        image.dataset.zoomInitialized = true;
      }
      // No magnifying lens on main images - only in modal
    });

    // Reinitialize when the DOM changes (for dynamically loaded content)
    this.setupMutationObserver();
  }

  isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
  }

  addMagnifyingLensToModal(modalImg) {
    const container = modalImg.parentElement;
    
    // Remove existing lens if it exists
    const existingLens = container.querySelector('.magnifying-lens');
    if (existingLens) existingLens.remove();
    
    // Create lens element
    const lens = document.createElement('div');
    lens.className = 'magnifying-lens modal-lens';
    lens.style.display = 'none';
    container.appendChild(lens);

    let isLensActive = false;

    // Desktop events
    modalImg.addEventListener('mouseenter', () => {
      if (!this.isMobile() && !isLensActive) {
        lens.style.display = 'block';
        modalImg.classList.add('lens-active');
        isLensActive = true;
      }
    });

    modalImg.addEventListener('mouseleave', () => {
      if (!this.isMobile()) {
        lens.style.display = 'none';
        modalImg.classList.remove('lens-active');
        isLensActive = false;
      }
    });

    modalImg.addEventListener('mousemove', (e) => {
      if (isLensActive && !this.isMobile()) {
        this.moveLens(e, modalImg, lens);
      }
    });

    // Mobile touch events
    modalImg.addEventListener('touchstart', (e) => {
      if (this.isMobile()) {
        e.preventDefault();
        lens.style.display = 'block';
        modalImg.classList.add('lens-active');
        isLensActive = true;
        this.moveLensMobile(e.touches[0], modalImg, lens);
      }
    });

    modalImg.addEventListener('touchmove', (e) => {
      if (this.isMobile() && isLensActive) {
        e.preventDefault();
        this.moveLensMobile(e.touches[0], modalImg, lens);
      }
    });

    modalImg.addEventListener('touchend', (e) => {
      if (this.isMobile()) {
        e.preventDefault();
        setTimeout(() => {
          lens.style.display = 'none';
          modalImg.classList.remove('lens-active');
          isLensActive = false;
        }, 300); // Reduced delay for better responsiveness
      }
    });

    // Prevent scrolling when touching the image
    modalImg.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: false });

    modalImg.addEventListener('touchmove', (e) => {
      e.stopPropagation();
    }, { passive: false });
  }

  moveLens(e, image, lens) {
    const rect = image.getBoundingClientRect();
    const lensSize = 180;
    const zoomFactor = 1.8;
    
    // Get cursor position relative to image
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    
    // Calculate lens position (center the lens on cursor)
    let x = cursorX - lensSize / 2;
    let y = cursorY - lensSize / 2;
    
    // Keep lens within image boundaries
    x = Math.max(0, Math.min(x, rect.width - lensSize));
    y = Math.max(0, Math.min(y, rect.height - lensSize));
    
    // Position the lens
    lens.style.left = x + 'px';
    lens.style.top = y + 'px';
    
    // Calculate what part of the image should be shown in the lens
    // The lens shows the area under the cursor, magnified
    const lensCenter = {
      x: x + lensSize / 2,
      y: y + lensSize / 2
    };
    
    // Calculate background position to show the magnified area
    const bgX = -(lensCenter.x * zoomFactor - lensSize / 2);
    const bgY = -(lensCenter.y * zoomFactor - lensSize / 2);
    
    // Apply the zoomed image as background of the lens
    lens.style.backgroundImage = `url(${image.src})`;
    lens.style.backgroundSize = `${rect.width * zoomFactor}px ${rect.height * zoomFactor}px`;
    lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
    lens.style.backgroundRepeat = 'no-repeat';
  }

  moveLensMobile(touch, image, lens) {
    const rect = image.getBoundingClientRect();
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // Responsive lens size based on screen size
    let lensSize = screenWidth < 480 ? 140 : screenWidth < 768 ? 160 : 180;
    const zoomFactor = screenWidth < 480 ? 1.6 : 1.8;
    
    // Get touch position relative to the viewport
    const touchX = touch.clientX;
    const touchY = touch.clientY;
    
    // Calculate position relative to image
    const cursorX = touchX - rect.left;
    const cursorY = touchY - rect.top;
    
    // Smart positioning to avoid finger covering lens
    const fingerOffset = screenWidth < 480 ? 60 : 70;
    let x = cursorX - lensSize / 2;
    let y = cursorY - lensSize / 2 - fingerOffset;
    
    // If lens would go above screen, position it below finger instead
    if (touchY - fingerOffset - lensSize / 2 < 20) {
      y = cursorY - lensSize / 2 + fingerOffset;
    }
    
    // Keep lens within image boundaries with padding
    const padding = 10;
    x = Math.max(padding, Math.min(x, rect.width - lensSize - padding));
    y = Math.max(padding, Math.min(y, rect.height - lensSize - padding));
    
    // Update lens size dynamically
    lens.style.width = lensSize + 'px';
    lens.style.height = lensSize + 'px';
    
    // Position the lens
    lens.style.left = x + 'px';
    lens.style.top = y + 'px';
    
    // The lens should show the area under the actual touch point
    const actualTouchCenter = {
      x: cursorX,
      y: cursorY
    };
    
    // Calculate background position to show the magnified area under the actual touch
    const bgX = -(actualTouchCenter.x * zoomFactor - lensSize / 2);
    const bgY = -(actualTouchCenter.y * zoomFactor - lensSize / 2);
    
    // Apply the zoomed image as background of the lens
    lens.style.backgroundImage = `url(${image.src})`;
    lens.style.backgroundSize = `${rect.width * zoomFactor}px ${rect.height * zoomFactor}px`;
    lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
    lens.style.backgroundRepeat = 'no-repeat';
  }

  setupMutationObserver() {
    // Watch for changes in the DOM to handle dynamically loaded images
    const observer = new MutationObserver((mutations) => {
      let shouldReinit = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 && (
                node.classList.contains('viewer-holder') || 
                node.querySelector('.viewer-holder')
            )) {
              shouldReinit = true;
            }
          });
        }
      });
      
      if (shouldReinit) {
        this.updateZoomableImages();
      }
    });
    
    // Start observing the document with the configured parameters
    observer.observe(document.body, { childList: true, subtree: true });
  }

  updateZoomableImages() {
    // Get all images that should be zoomable
    const newImages = document.querySelectorAll('.viewer-holder img.product-image');
    
    // Add zoom functionality to new images
    newImages.forEach(image => {
      if (!image.classList.contains('zoomable-image')) {
        image.classList.add('zoomable-image');
        image.addEventListener('click', this.openZoom.bind(this));
        
        // No magnifying lens on main images - only in modal
      }
    });
    
    // Update our list of zoomable images
    this.zoomableImages = newImages;
  }

  createModal() {
    const modal = document.createElement('div');
    modal.id = this.modalId;
    modal.className = 'image-zoom-modal';
    
    modal.innerHTML = `
      <div class="image-zoom-modal-content">
        <div class="zoom-controls">
          <span class="image-zoom-close" title="Close">&times;</span>
        </div>
        <div class="zoom-container">
          <img class="image-zoom-img" src="" alt="" draggable="false">
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Add all event listeners
    this.setupModalEvents(modal);
  }

  setupModalEvents(modal) {
    const closeBtn = modal.querySelector('.image-zoom-close');
    const zoomContainer = modal.querySelector('.zoom-container');
    const modalImg = modal.querySelector('.image-zoom-img');
    const modalContent = modal.querySelector('.image-zoom-modal-content');

    // Close functionality
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closeZoom();
    });

    // Close when clicking on the modal background (black area)
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeZoom();
      }
    });

    // Also close when clicking on the modal content area but not on the image
    modalContent.addEventListener('click', (e) => {
      if (e.target === modalContent || e.target === zoomContainer) {
        this.closeZoom();
      }
    });

    // Prevent closing when clicking on the image itself
    modalImg.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // Touch events for mobile pinch-to-zoom
    this.setupTouchEvents(zoomContainer, modalImg);

    // Drag functionality
    this.setupDragEvents(modalImg);

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (modal.style.display === 'flex') {
        switch(e.key) {
          case 'Escape':
        this.closeZoom();
            break;
        }
      }
    });
  }

  setupTouchEvents(container, img) {
    let initialDistance = 0;
    let initialZoom = 1;

    container.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        initialDistance = this.getDistance(e.touches[0], e.touches[1]);
        initialZoom = this.zoomLevel;
      }
    });

    container.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (e.touches.length === 2) {
        const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
        const scale = currentDistance / initialDistance;
        const newZoom = initialZoom * scale;
        
        if (newZoom >= this.minZoom && newZoom <= this.maxZoom) {
          this.zoomLevel = newZoom;
          this.applyZoom();
        }
      }
    });
  }

  setupDragEvents(img) {
    img.addEventListener('mousedown', (e) => {
      if (this.zoomLevel > 1) {
        this.isDragging = true;
        this.lastMousePos = { x: e.clientX, y: e.clientY };
        img.style.cursor = 'grabbing';
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (this.isDragging && this.zoomLevel > 1) {
        const deltaX = e.clientX - this.lastMousePos.x;
        const deltaY = e.clientY - this.lastMousePos.y;
        
        this.imageOffset.x += deltaX;
        this.imageOffset.y += deltaY;
        
        this.lastMousePos = { x: e.clientX, y: e.clientY };
        this.applyZoom();
      }
    });

    document.addEventListener('mouseup', () => {
      this.isDragging = false;
      img.style.cursor = this.zoomLevel > 1 ? 'grab' : 'default';
    });
  }

  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  zoomImage(delta) {
    const newZoom = this.zoomLevel + delta;
    if (newZoom >= this.minZoom && newZoom <= this.maxZoom) {
      this.zoomLevel = newZoom;
      this.applyZoom();
    }
  }

  resetZoom() {
    this.zoomLevel = 1;
    this.imageOffset = { x: 0, y: 0 };
    this.applyZoom();
  }

  applyZoom() {
    const modal = document.getElementById(this.modalId);
    const img = modal.querySelector('.image-zoom-img');
    
    img.style.transform = `scale(${this.zoomLevel}) translate(${this.imageOffset.x / this.zoomLevel}px, ${this.imageOffset.y / this.zoomLevel}px)`;
    img.style.cursor = this.zoomLevel > 1 ? 'grab' : 'default';
  }

  openZoom(event) {
    event.preventDefault();
    const modal = document.getElementById(this.modalId);
    const modalImg = modal.querySelector('.image-zoom-img');
    
    // Reset zoom state
    this.zoomLevel = 1;
    this.imageOffset = { x: 0, y: 0 };
    
    // Get high resolution image if available
    const image = event.target;
    const highResSrc = image.getAttribute('data-high-res-url') || image.src;
    
    // Set image source and alt text
    modalImg.src = highResSrc;
    modalImg.alt = image.alt;
    modalImg.style.transform = 'scale(1) translate(0px, 0px)';
    
    // Add magnifying lens functionality to the modal image
    this.addMagnifyingLensToModal(modalImg);
    
    // Show modal with a slight delay to allow for transition
    setTimeout(() => {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden'; // Prevent scrolling
    }, 10);
  }
  
  closeZoom() {
    const modal = document.getElementById(this.modalId);
    modal.style.display = 'none';
    document.body.style.overflow = ''; // Restore scrolling
    
    // Reset zoom state
    this.zoomLevel = 1;
    this.imageOffset = { x: 0, y: 0 };
    
    // Clean up lens elements
    const container = modal.querySelector('.zoom-container');
    const lens = container.querySelector('.magnifying-lens.modal-lens');
    if (lens) lens.remove();
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductImageZoom();
});

// Re-initialize when Shopify section is reloaded
document.addEventListener('shopify:section:load', () => {
  new ProductImageZoom();
}); 